import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { MessageSquare, ArrowUp, TrendingUp } from "lucide-react";

/**
 * Compact widget that shows the top 3 community posts about a specific
 * ticker. Rendered in the Dashboard sidebar whenever the user has a
 * ticker selected (via row-click or chart focus).
 */
function timeAgo(ts) {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function TickerCommunityWidget({ ticker }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) { setPosts([]); return; }
    setLoading(true);
    api.get(`/api/community/trending-for-ticker/${encodeURIComponent(ticker)}?limit=3`)
      .then(({ data }) => setPosts(data?.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (!ticker) return null;

  return (
    <div className="card-surface p-4 relative overflow-hidden" data-testid={`trending-widget-${ticker}`}>
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 55%)" }}
      />
      <div className="relative">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[var(--bull)]" />
            <div className="text-white text-[13px] font-semibold tracking-tight">
              Trending: <span className="font-mono">${ticker}</span>
            </div>
          </div>
          <Link
            to={`/community?q=${ticker}`}
            className="label-mono hover:text-white transition-colors"
            data-testid="trending-widget-more"
          >
            More →
          </Link>
        </div>

        {loading && (
          <div className="text-white/40 text-[12px] py-3 text-center">Looking for chatter…</div>
        )}
        {!loading && posts.length === 0 && (
          <div className="text-white/50 text-[12px] py-3 leading-relaxed">
            No community posts about <span className="font-mono">${ticker}</span> in the last 14 days.
            {" "}<Link to={`/community/new?community=stocks`}
                      className="text-[var(--bull)] hover:underline"
                      data-testid="trending-widget-first">Be the first →</Link>
          </div>
        )}
        {!loading && posts.length > 0 && (
          <div className="space-y-2">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/community/post/${p.id}`}
                className="block group -mx-1 px-1 py-1.5 rounded-lg hover:bg-white/[0.03] transition-colors"
                data-testid={`trending-post-${p.id}`}
              >
                <div className="text-white/90 text-[12.5px] font-medium leading-snug line-clamp-2 group-hover:text-white">
                  {p.title}
                </div>
                <div className="mt-1 flex items-center gap-2 label-mono">
                  <span className="text-white/70">r/{p.community_slug}</span>
                  <span>·</span>
                  <span>@{p.author_username}</span>
                  <span>·</span>
                  <span>{timeAgo(p.created_at)}</span>
                  <span className="ml-auto inline-flex items-center gap-1">
                    <ArrowUp className="w-3 h-3 text-[var(--bull)]" />{p.score || 0}
                    <MessageSquare className="w-3 h-3 ml-1" />{p.comment_count || 0}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
